<!--
📌 Componente: AktionButton.vue
📂 Caminho: src/components/AktionButton.vue
📝 Descrição: Botão reutilizável com prevenção robusta de duplo disparo
    por throttle, suporte opcional a v-model:busy e bloqueio de
    keydown repetido (Enter/Espaço). Único caminho de disparo.

👁️ Interface e Interação com o Usuário:
  - Clique ou teclado (Enter/Espaço) disparam a ação uma única vez.
  - Estado "ocupado" visual e ARIA enquanto bloqueado.
  - Throttle temporal evita múltiplos disparos consecutivos.

📥 Entrada (Props):
  - `disabled` (Boolean) - Desativa o botão.
  - `icon` (Boolean) - Exibe ícone SVG por tipo.
  - `label` (String) - Texto exibido (padrão: "Aktion").
  - `type` (String) - Estilo/ícone do botão.
  - `busy` (Boolean) - Controle externo opcional via v-model:busy.
  - `throttleMs` (Number) - Janela de bloqueio (padrão: 800).

📤 Saída (Emits):
  - `@click` - Único disparo por janela de throttle.
  - `update:busy` - Emissão quando controlar busy externamente.

🔗 Dependências: Nenhuma.

🎯 Notas de A11y:
  - `aria-busy` e `aria-disabled` refletem bloqueio efetivo.
  - Enter/Espaço usam keydown com `.prevent` e sem repetição.

🎨 Estilos e Cores Disponíveis (`type`):
  Cores vêm só dos tokens de tailwind.config.js (doc_DESIGN.md).
  - `speichern` (Salvar) → 🟢 bg-verde-suave text-branco hover:bg-verde-profundo
  - `bearbeiten` (Editar) → 🔵 bg-azul-profundo text-branco hover:bg-azul-escuro
  - `löschen` (Excluir) → 🔴 bg-vermelho-suave text-branco hover:bg-vermelho-profundo
  - `warnen` (Aviso) → 🟠 bg-amarelo-profundo text-branco hover:bg-amarelo-profundo/90
  - `schwarz` (Preto) → ⚫ bg-cinza-900 text-branco hover:bg-cinza-900/90
  - `indigo` (Índigo) → 🔵 bg-azul-escuro text-branco hover:bg-azul-escuro/90
  - `violett` (Roxo) → 🟣 bg-roxo text-branco hover:bg-roxo/90
  - `lima` (Verde Lima) → 🟢 bg-verde-suave text-branco hover:bg-verde-profundo
  - `hell` (Claro) → ⚪ bg-branco text-cinza-900 hover:bg-cinza-claro border border-borda-suave
  - `suchen` (Pesquisar) → 🟢 bg-verde-suave text-branco hover:bg-verde-profundo
  - `reparaturschein` → 🟪 bg-lilas text-texto-principal hover:brightness-95
  - `azul` (Azul Profundo) → 🔵 bg-azul-profundo text-branco hover:bg-verde-profundo
    ⚠️ Aplica `capitalize`: em rótulo de duas palavras o alemão sai errado
    ("Neu anlegen" vira "Neu Anlegen"). Para rótulo composto use outro tipo.
  - `veroeffentlichen` (Publicar) → 🔵 bg-azul-profundo, hover do próprio azul,
    sem `capitalize`. Ícone de globo. Criado para o módulo Website Praxis GmbH.
  - `abmelden` (Sair) → 🔴 bg-vermelho-suave text-branco hover:bg-vermelho-suave/90
  - `default` (Padrão) → ⚪ bg-cinza-500 text-branco hover:bg-texto-secundario
-->

<template>
  <button
    type="button"
    :class="buttonClass"
    :disabled="isLocked"
    :aria-busy="isBusy"
    :aria-disabled="isLocked"
    @click.stop.prevent="onTrigger()"
    @keydown.enter.prevent="onKeydown($event)"
    @keydown.space.prevent="onKeydown($event)"
  >
    <span class="flex items-center space-x-2">
      <svg
        v-if="icon"
        :class="iconClass"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <template v-if="Array.isArray(iconPath)">
          <path v-for="(d, i) in iconPath" :key="i" :d="d" />
        </template>
        <path v-else :d="iconPath" />
      </svg>
      <span>{{ displayLabel }}</span>
    </span>
  </button>
</template>

<script>
export default {
  name: "AktionButton",
  props: {
    disabled: { type: Boolean, default: false },
    icon: { type: Boolean, default: false },
    label: { type: String, default: null },
    type: { type: String, default: "default" },

    // ✅ v-model:busy opcional (controlado externamente)
    // Se ausente, o componente gerencia busy internamente.
    busy: { type: Boolean, default: undefined },

    // ✅ throttle temporal
    throttleMs: {
      type: Number,
      default: 800,
      validator: (v) => v >= 0 && v <= 10000,
    },
  },
  emits: ["click", "update:busy"],
  data() {
    return {
      internalBusy: false, // usado quando v-model não for utilizado
      throttleLock: false,
      timers: {
        throttle: null,
        autoBusy: null,
      },
    };
  },
  computed: {
    displayLabel() {
      return this.label ?? this.$t("buttons.action");
    },
    isControlled() {
      return this.busy !== undefined;
    },
    isBusy() {
      return this.isControlled ? !!this.busy : this.internalBusy;
    },
    isLocked() {
      return this.disabled || this.isBusy || this.throttleLock;
    },
    buttonClass() {
      return [
        "min-h-[48px] py-2 px-5 rounded shadow-md transition font-semibold",
        "flex items-center justify-center",
        "hover:scale-105 active:scale-95 transform duration-200",
        this.isLocked
          ? "bg-cinza-400 text-cinza-700 cursor-not-allowed"
          : this.buttonStyle,
      ];
    },
    buttonStyle() {
      const styles = {
        abmelden: "bg-vermelho-suave text-branco hover:bg-vermelho-suave/90",
        azul: "bg-azul-profundo text-branco capitalize hover:bg-verde-profundo",
        bearbeiten: "bg-azul-profundo text-branco hover:bg-azul-escuro",
        default: "bg-cinza-500 text-branco hover:bg-texto-secundario",
        hell: "bg-branco text-cinza-900 hover:bg-cinza-claro border border-borda-suave",
        indigo: "bg-azul-escuro text-branco hover:bg-azul-escuro/90",
        lima: "bg-verde-suave text-branco hover:bg-verde-profundo",
        löschen: "bg-vermelho-suave text-branco hover:bg-vermelho-profundo",
        schwarz: "bg-cinza-900 text-branco hover:bg-cinza-900/90",
        speichern: "bg-verde-suave text-branco hover:bg-verde-profundo",
        suchen: "bg-verde-suave text-branco hover:bg-verde-profundo",
        "scan-start": "bg-verde-suave text-branco hover:bg-verde-profundo",
        violett: "bg-roxo text-branco hover:bg-roxo/90",
        warnen: "bg-amarelo-profundo text-branco hover:bg-amarelo-profundo/90",
        reparaturschein: "bg-lilas text-texto-principal hover:brightness-95",
        upload: "bg-azul-profundo text-branco hover:bg-azul-profundo/90",
        veroeffentlichen:
          "bg-azul-profundo text-branco hover:bg-azul-profundo/90",
        zurueck: "bg-amarelo-profundo text-branco hover:bg-amarelo-profundo/90",
        anhan:
          "bg-branco text-texto-principal border border-borda-suave hover:bg-cinza-claro",
        "loeschen-soft":
          "bg-vermelho-fundo text-vermelho-profundo hover:bg-vermelho-suave/20 border border-vermelho-suave/50",
        cancelar: "bg-vermelho-suave text-branco hover:bg-vermelho-profundo",
        rejeitar:
          "bg-amarelo-profundo text-branco hover:bg-amarelo-profundo/90",
        "finalizar-coleta":
          "bg-azul-profundo text-branco hover:bg-azul-profundo/90",
      };
      return styles[this.type] || styles.default;
    },
    iconPath() {
      const icons = {
        // 1) abmelden
        abmelden: [
          "M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3",
          "-3V7a3 3 0 013-3h4a3 3 0 013 3v1",
        ].join(""),
        // 1.1) anhan (clipe)
        anhan: [
          "M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.414a4 4 0 00-5.656-5.656l-6.415 6.414a6 6 0 108.486 8.486L20.5 13",
        ].join(""),
        // 2) azul
        azul: "M12 4v16m8-8H4",
        // 3) bearbeiten
        bearbeiten: [
          "M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 11",
          "3.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z",
        ].join(""),
        // 4) default (novo)
        default: [
          "M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6",
          " 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2",
          " 2 0 002 2z",
        ].join(""),
        // 5) hell
        hell: "M12 4v16m8-8H4",
        // 6) indigo
        indigo: "M5 13l4 4L19 7",
        // 7) lima
        lima: "M4 8h16M4 16h16",
        // 8) löschen
        löschen: [
          "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01",
          "-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1",
          " 1 0 00-1 1v3M4 7h16",
        ].join(""),
        // 9) schwarz (novo)
        schwarz: [
          "M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4",
          "-4m4 4V4",
        ].join(""),
        // 10) speichern
        speichern: "M5 13l4 4L19 7",
        // 11) suchen
        suchen: ["M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"].join(""),
        // 12) violett
        violett: "M12 4v16m8-8H4",
        // 13) warnen
        warnen: "M12 2L2 22h20L12 2z",
        // 14) reparaturschein
        reparaturschein: [
          "M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 01",
          "18 0z",
        ].join(""),
        // 15) loeschen-soft (reusa loeschen)
        "loeschen-soft": [
          "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01",
          "-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1",
          " 1 0 00-1 1v3M4 7h16",
        ].join(""),
        // 16) upload
        upload: [
          "M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8",
          "m4-4v12",
        ].join(""),
        // 17) zurueck
        zurueck: ["M10 19l-7-7m0 0l7-7m-7 7h18"].join(""),
        // 18) cancelar
        cancelar: "M6 18L18 6M6 6l12 12",
        // 19) rejeitar
        rejeitar: [
          "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01",
          "-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1",
          " 1 0 00-1 1v3M4 7h16",
        ].join(""),
        // 20) finalizar-coleta
        "finalizar-coleta": "M5 13l4 4L19 7",
        // 21) veroeffentlichen (globo: publicar na web)
        veroeffentlichen: [
          "M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9",
          " 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1",
          ".343-9 3-9m-9 9a9 9 0 019-9",
        ].join(""),
      };
      return icons[this.type] || icons.default;
    },
    iconClass() {
      return "w-5 h-5";
    },
  },
  methods: {
    // 🔒 único caminho de disparo
    onTrigger() {
      if (this.isLocked) return;

      // ativa throttle imediatamente
      this.throttleLock = true;
      clearTimeout(this.timers.throttle);
      this.timers.throttle = setTimeout(() => {
        this.throttleLock = false;
      }, this.throttleMs);

      // marca busy (interno) ou solicita controle externo
      this.setBusy(true);

      // emite evento uma única vez
      this.$emit("click");

      // se não controlado externamente, libera busy ao fim do throttle
      if (!this.isControlled) {
        clearTimeout(this.timers.autoBusy);
        this.timers.autoBusy = setTimeout(() => {
          this.setBusy(false);
        }, this.throttleMs);
      }
    },

    // ⌨️ bloqueio de repetição por teclado
    onKeydown(evt) {
      if (evt && evt.repeat) return;
      this.onTrigger();
    },

    setBusy(val) {
      if (this.isControlled) {
        this.$emit("update:busy", !!val);
      } else {
        this.internalBusy = !!val;
      }
    },
  },
  beforeUnmount() {
    clearTimeout(this.timers.throttle);
    clearTimeout(this.timers.autoBusy);
  },
};
</script>

<style scoped>
button {
  outline: none;
}
</style>
